import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import SidebarEditor from '@/components/dev-builder/SidebarEditor';
import PreviewPane from '@/components/dev-builder/PreviewPane';

// dev-builder-200 Fase 3: editor de UNA landing (identificada por :slug), ya en
// modelo "1 landing por desarrollo". La company se lee de useAuth() (viaja
// completa en el login, routes/auth.py:291): no hace falta GET /api/company.

export default function DevBuilderPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const company = user?.company || {};
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const [landing, setLanding] = useState(null);
  const [originalLanding, setOriginalLanding] = useState(null);
  const [development, setDevelopment] = useState(null);
  const [units, setUnits] = useState([]);
  const [developments, setDevelopments] = useState([]);

  const [expandedSections, setExpandedSections] = useState({
    hero: true, proyectos: true, disponibilidad: false, caracteristicas: false,
    galeria: false, faq: false, contacto: false, redes: false, footer: false,
    tipografia: false, permisos: false,
  });

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const landingRes = await api.get(`/dev-builder/landings/${slug}`);
      const landingData = landingRes.data;
      setLanding(landingData);
      setOriginalLanding(JSON.parse(JSON.stringify(landingData)));

      const [devDetailRes, devsRes] = await Promise.all([
        api.get(`/dev-builder/data/developments/${landingData.development_id}`),
        api.get('/dev-builder/data/developments'),
      ]);
      setDevelopment(devDetailRes.data.development);
      setUnits(devDetailRes.data.units || []);
      setDevelopments(devsRes.data.developments || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error cargando la landing');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { if (slug) load(); }, [slug]);

  const updateSection = (sectionName, updates) => {
    setLanding((prev) => ({ ...prev, [sectionName]: { ...prev[sectionName], ...updates } }));
  };

  const toggleSection = (sectionName) => {
    setExpandedSections((prev) => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  const hasChanges = JSON.stringify(landing) !== JSON.stringify(originalLanding);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await api.put(`/dev-builder/landings/${slug}`, landing);
      setOriginalLanding(JSON.parse(JSON.stringify(landing)));
      toast.success('Landing guardada');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error guardando la landing';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async (nextPublished) => {
    try {
      setIsSaving(true);
      await api.post(`/dev-builder/landings/${slug}/publish`, null, { params: { published: nextPublished } });
      setLanding((prev) => ({ ...prev, is_published: nextPublished }));
      setOriginalLanding((prev) => (prev ? { ...prev, is_published: nextPublished } : prev));
      toast.success(nextPublished ? 'Landing publicada' : 'Landing despublicada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error publicando la landing');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar la landing de "${landing.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/dev-builder/landings/${slug}`);
      toast.success('Landing eliminada');
      navigate('/dev-builder');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando la landing');
    }
  };

  if (isLoading) return <div className="p-8 text-center">Cargando...</div>;
  if (!landing) return <div className="p-8 text-center text-red-600">{error || 'Landing no encontrada'}</div>;

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="w-96 border-r border-slate-700 bg-slate-800 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-slate-800 border-b border-slate-700 p-4">
          <button onClick={() => navigate('/dev-builder')} className="text-sm text-gray-300 flex items-center gap-1 mb-2 hover:text-gray-100">
            <ArrowLeft className="h-4 w-4" /> Volver
          </button>
          <h1 className="text-xl font-bold mb-1 text-gray-100">{landing.name}</h1>
          <p className="text-sm text-gray-400">/dev-sites/{landing.slug}</p>
        </div>

        {error && (
          <Alert variant="destructive" className="m-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="p-4 border-b border-slate-700 space-y-2">
          <Button onClick={handleSave} disabled={!hasChanges || isSaving} variant="default" className="w-full">
            {isSaving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
          {isAdmin && (
            <Button onClick={() => handlePublish(!landing.is_published)} disabled={isSaving} variant="outline" className="w-full">
              {landing.is_published ? 'Despublicar' : 'Publicar'}
            </Button>
          )}
        </div>

        <SidebarEditor
          landing={landing}
          developments={developments}
          units={units}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          updateSection={updateSection}
          onDelete={handleDelete}
          userRole={user?.role}
        />
      </div>

      <div className="flex-1 overflow-hidden bg-gray-100 p-6">
        <PreviewPane landing={landing} development={development} units={units} company={company} />
      </div>
    </div>
  );
}
